import { useCallback, useMemo, useState } from 'react';
import { type AvatarProps, Channel, MessageInput, MessageList, useChatContext, Window } from 'stream-chat-react';

import { getUsers } from '@/data/user/selectors';
import { getAuthAccount, getAuthUser } from '@/shared/auth/selectors';
import Avatar from '@/shared/ui/Avatar';
import FontIcon from '@/shared/ui/FontIcon';
import SpinningLoader from '@/shared/ui/loaders/SpinningLoader';
import { useWiwDispatch, useWiwSelector } from '@/store';
import { STREAM_CHANNEL_TYPES, WORKCHAT_VIEWS } from '@/workchat/constants';
import { useStreamProvider } from '@/workchat/v2/providers/StreamProvider';
import { viewStackPop, viewStackPush } from '@/workchat/v2/store/reducer';
import ConversationTitleV3 from '@/workchat/v3/components/ConversationTitleV3/ConversationTitleV3';
import HeaderV3, { type HeaderButton } from '@/workchat/v3/components/HeaderV3/HeaderV3';
import { MessageV3 } from '@/workchat/v3/components/MessageV3/MessageV3';

import '@/workchat/v3/components/ViewConversationV3/ViewConversationV3.scss';

export default function ViewConversationV3() {
  const dispatch = useWiwDispatch();
  const user = useWiwSelector(getAuthUser);
  const account = useWiwSelector(getAuthAccount);
  const users = useWiwSelector(getUsers);

  const getUserById = useCallback((userId: number) => users.get(userId), [users]);

  // TODO
  // const connected = useWiwSelector(getWorkchatConnected);
  const { client } = useStreamProvider();

  // TODO
  const [_error, _setError] = useState<string | null>(null);

  const { channel } = useChatContext();

  const buttons: HeaderButton[] = [
    {
      title: 'Back',
      color: 'secondary',
      action: () => {
        dispatch(viewStackPop());
      },
      label: [<FontIcon className="mr-1" key="back" icon="chevron-left" />, 'Back'],
      side: 'left',
    },
  ];

  if (channel && channel.type !== STREAM_CHANNEL_TYPES.BROADCAST) {
    const label =
      channel.type === STREAM_CHANNEL_TYPES.CHAT ? (
        <FontIcon icon="gear" srText={'View Conversation Details'} />
      ) : (
        <FontIcon icon="info" srText={'View Members'} />
      );

    buttons.push({
      title: 'Details',
      action: () => dispatch(viewStackPush(WORKCHAT_VIEWS.VIEW_CONVERSATION_DETAIL)),
      label,
      side: 'right',
      circle: true,
      color: 'secondary',
    });
  }

  const sendingAllowed = useMemo(() => {
    if (channel && channel.type === STREAM_CHANNEL_TYPES.BROADCAST) {
      const broadcast = account.settings?.workchat?.managedChannelConversationsAllowed;
      return broadcast || user.canManage();
    }

    return account.settings?.workchat?.customChannelsAllowed;
  }, [channel]);

  if (!client || !channel) {
    return (
      <div className="ViewConversationV3">
        <HeaderV3 borderBottom buttons={buttons}>
          <div>Loading conversation...</div>
        </HeaderV3>
        <div className="loading-container">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="ViewConversationV3">
      <HeaderV3 borderBottom buttons={buttons}>
        <ConversationTitleV3
          conversationType={channel.type as STREAM_CHANNEL_TYPES}
          conversationName={channel.data?.name}
          // TODO: WIWJS-9679 - Message View - Header
          inConversation
        />
      </HeaderV3>
      <Channel
        Message={MessageV3}
        LoadingIndicator={() => <SpinningLoader className="spinning-loader" color="gray" height={24} width={24} />}
        Avatar={(props: AvatarProps) => {
          if (props.user?.id) {
            const chatUser = getUserById(+props.user.id);
            return <Avatar user={chatUser} size="micro" />;
          }
        }}
        AttachmentSelectorInitiationButtonContents={() => (
          <FontIcon className="message-input-add-photo-icon" icon="camera" srText="Add photo" />
        )}
      >
        <Window>
          <MessageList />
          {sendingAllowed && <MessageInput additionalTextareaProps={{ placeholder: 'Type a message...' }} />}
        </Window>
      </Channel>
    </div>
  );
}
